import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Plus } from "lucide-react";
import { DEFAULT_EXPENSE_TYPES, DEFAULT_PAID_BY_OPTIONS, DEFAULT_TAX_CATEGORIES } from "@/hooks/useExpenses";

interface ManageOptionsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  type: "expense_type" | "paid_by" | "tax_category";
  options: string[];
  onAdd: (value: string) => void;
}

export const ManageOptionsDialog = ({
  isOpen,
  onClose,
  type,
  options,
  onAdd,
}: ManageOptionsDialogProps) => {
  const [newOption, setNewOption] = useState("");

  const defaults =
    type === "expense_type" ? DEFAULT_EXPENSE_TYPES
    : type === "tax_category" ? DEFAULT_TAX_CATEGORIES
    : DEFAULT_PAID_BY_OPTIONS;

  const customOptions = options.filter((opt) => !defaults.includes(opt));

  const handleAdd = () => {
    if (newOption.trim() && !options.includes(newOption.trim())) {
      onAdd(newOption.trim());
      setNewOption("");
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAdd();
    }
  };

  const title = type === "expense_type" ? "Expense Types"
    : type === "tax_category" ? "Tax Categories"
    : "Paid By Options";

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            Manage {title}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <h4 className="text-sm font-medium mb-2 text-muted-foreground">
              Default Options
            </h4>
            <div className="flex flex-wrap gap-2 max-h-[200px] overflow-y-auto">
              {defaults.map((opt) => (
                <Badge key={opt} variant="secondary">
                  {opt}
                </Badge>
              ))}
            </div>
          </div>

          {customOptions.length > 0 && (
            <div>
              <h4 className="text-sm font-medium mb-2 text-muted-foreground">
                Custom Options
              </h4>
              <div className="flex flex-wrap gap-2">
                {customOptions.map((opt) => (
                  <Badge key={opt} variant="outline">
                    {opt}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          <div className="flex gap-2">
            <Input
              placeholder="Add new option..."
              value={newOption}
              onChange={(e) => setNewOption(e.target.value)}
              onKeyDown={handleKeyDown}
            />
            <Button onClick={handleAdd} size="icon">
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
